import { useEffect, useRef, useState } from "preact/hooks";
import type { ChatMessage, ProductCard as ProductCardT, IngredientCard as IngredientCardT } from "./types.ts";
import { getSessionToken, streamChat } from "./api.ts";
import { chatStore, detectProductHandle } from "./store.ts";
import { renderMarkdown } from "./markdown.tsx";
import { addToCartByHandle, CartAddError } from "./cart.ts";
import { track, type AnalyticsSource } from "./analytics.ts";

// Wrapper that returns undefined instead of null so we can pass the result
// straight through to streamChat (which uses optional, not nullable).
function detectProductHandleSafe(): string | undefined {
  return detectProductHandle() ?? undefined;
}

// Shoppers never see raw API/HTTP errors. The technical message still goes
// to the console and the analytics "error" event for debugging.
// Shopify's storefront agent owns the floating chat bubble. The concierge
// opens only from the product-page block and #ai-chat links (AI Beauty nav).
const SHOW_FLOATING_BUBBLE = false;

const FRIENDLY_ERROR =
  "Sorry, I'm having trouble answering right now. Please try again in a few minutes, or text us at 323.212.5413 or email service@ampbeautyla.com.";

function showError(setError: (msg: string) => void, detail: string): void {
  console.warn("[amp-chat]", detail);
  setError(FRIENDLY_ERROR);
}

/**
 * Top-level chat widget. Owns the open/closed state, message log,
 * and the streaming connection. Mounts inside a shadow root, so the
 * `style` is provided by the host (Widget.tsx is rendered into a
 * shadow DOM with the global stylesheet already applied).
 */
export function Widget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<string | null>(null);

  // Lazy-fetch the session token on first open. Avoids a network call for
  // visitors who never click the bubble.
  useEffect(() => {
    if (!open || sessionRef.current) return;
    let cancelled = false;
    getSessionToken()
      .then((token) => {
        if (!cancelled) sessionRef.current = token;
      })
      .catch((err) => {
        if (!cancelled) showError(setError, `Could not start chat: ${err instanceof Error ? err.message : "unknown"}`);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Helper: opens the modal AND fires the analytics beacon with a source
  // label. Centralizes the open paths so we never forget to track one.
  function openModal(source: AnalyticsSource): void {
    setOpen(true);
    track("modal_open", {
      source,
      productHandle: detectProductHandleSafe(),
    });
  }

  // Listen for chat requests from the inline PDP block. When one comes
  // in, open the modal and auto-send the message — carrying the
  // productHandle through so the agent has product context.
  useEffect(() => {
    const unsub = chatStore.subscribe(() => {
      const req = chatStore.consumeRequest();
      if (!req) return;
      // The chatStore request comes from PdpInline. We can't tell from the
      // request alone whether a chip or the send button fired it; PdpInline
      // already tracks the specific click. Modal-open source is "inline-pdp".
      openModal("inline-pdp-chip");
      // Defer a tick so the modal mounts and getSessionToken has a chance
      // to fire before we send.
      queueMicrotask(() => {
        void sendWithText(req.text, req.productHandle);
      });
    });
    return () => {
      unsub();
    };
    // sendWithText is stable across renders since it's a closure over refs
    // and setState; intentionally not in the dep array to avoid re-binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the modal when the URL signals it. Two channels:
  //   1. Hash `#ai-chat` — used by nav-menu "AI Beauty" links
  //   2. Query param `?chat=open` — used by marketing / email CTAs
  // This lets any link anywhere on the storefront open the chat without
  // needing to know about the widget's internal API.
  useEffect(() => {
    function shouldOpen(): boolean {
      if (typeof window === "undefined") return false;
      if (window.location.hash === "#ai-chat") return true;
      const qs = new URLSearchParams(window.location.search);
      return qs.get("chat") === "open";
    }
    function tryOpen() {
      if (shouldOpen()) openModal("hash-url");
    }
    // Initial check (handles links inside the same SPA-rendered page too).
    tryOpen();
    // Reactive: navigating to /#ai-chat from any link.
    window.addEventListener("hashchange", tryOpen);
    return () => {
      window.removeEventListener("hashchange", tryOpen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send(): Promise<void> {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    // Direct typing in the modal is page-level: we don't know which
    // product (if any) they're looking at. URL detection covers the
    // common case — same heuristic as the PDP mount logic.
    await sendWithText(text, detectProductHandleSafe());
  }

  async function sendWithText(text: string, productHandle?: string): Promise<void> {
    if (!text.trim() || sending) return;
    setError(null);

    if (!sessionRef.current) {
      try {
        sessionRef.current = await getSessionToken();
      } catch (err) {
        showError(setError, `Could not start chat: ${err instanceof Error ? err.message : "unknown"}`);
        track("error", {
          properties: {
            stage: "session-fetch",
            message: err instanceof Error ? err.message : "unknown",
          },
        });
        return;
      }
    }

    // Track the send. We deliberately do NOT record `text` itself; the
    // conversations table has it for QA. Bucket the length so we can see
    // "are shoppers typing short questions or essays?" without storing
    // PII or chat content twice.
    const lenBucket =
      text.length < 20
        ? "xs"
        : text.length < 60
        ? "s"
        : text.length < 200
        ? "m"
        : "l";
    track("message_send", {
      productHandle,
      properties: { length_bucket: lenBucket, has_product_context: !!productHandle },
    });

    const userId = `u-${Date.now()}`;
    const assistantId = `a-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: userId, sender: "user", text },
      { id: assistantId, sender: "assistant", text: "", toolNotes: [], cards: [] },
    ]);
    setSending(true);

    let buffer = "";
    let nextFlush = 0;
    // Track each unique product card as it streams in so per-product
    // recommendation analysis is possible. Set used to dedupe; a single
    // card can be emitted twice (search hit + details fetch).
    const seenCardHandles = new Set<string>();

    function flushTextSoon() {
      // Coalesce text-delta updates so we don't re-render on every tiny event.
      const now = performance.now();
      if (now < nextFlush) return;
      nextFlush = now + 33;
      const snapshot = buffer;
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, text: snapshot } : m))
      );
    }

    try {
      await streamChat(
        { sessionToken: sessionRef.current, message: text, productHandle },
        {
          onText: (chunk) => {
            buffer += chunk;
            flushTextSoon();
          },
          onToolStarted: (info) => {
            const note = formatToolNote(info);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, toolNotes: [...(m.toolNotes ?? []), note] } : m
              )
            );
          },
          onToolFinished: () => {
            // No-op — keep the start-note visible.
          },
          onProductCard: (card) => {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                const cards = m.cards ?? [];
                if (cards.find((c) => c.kind === "product" && c.data.handle === card.handle)) return m;
                return { ...m, cards: [...cards, { kind: "product", data: card }] };
              })
            );
            // Track each unique product card so we can answer "which
            // products does the AI surface most? does it ever surface
            // products that never get clicked?"
            if (!seenCardHandles.has(card.handle)) {
              seenCardHandles.add(card.handle);
              track("product_card_view", {
                productHandle: card.handle,
                properties: { position: seenCardHandles.size },
              });
            }
          },
          onIngredientCard: (card) => {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                const cards = m.cards ?? [];
                if (cards.find((c) => c.kind === "ingredient" && c.data.inci_name === card.inci_name)) return m;
                return { ...m, cards: [...cards, { kind: "ingredient", data: card }] };
              })
            );
          },
          onDone: (info) => {
            // Final flush in case the buffer had content not yet rendered.
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, text: buffer, done: true } : m
              )
            );
            track("assistant_done", {
              productHandle,
              properties: {
                cards: info?.cards ?? seenCardHandles.size,
                text_len_bucket:
                  buffer.length < 200 ? "s" : buffer.length < 1000 ? "m" : "l",
              },
            });
          },
          onError: (msg) => {
            showError(setError, msg);
            track("error", { properties: { stage: "stream", message: msg } });
          },
        }
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "unknown";
      showError(setError, `Stream error: ${msg}`);
      track("error", { properties: { stage: "stream-throw", message: msg } });
    } finally {
      setSending(false);
      // Defensive final flush.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId && m.text !== buffer ? { ...m, text: buffer, done: true } : m
        )
      );
    }
  }

  return (
    <>
      {!open && SHOW_FLOATING_BUBBLE && (
        <button class="bubble" aria-label="Open chat" onClick={() => openModal("floating-bubble")}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </button>
      )}

      {open && (
        <div class="modal" role="dialog" aria-label="AMP Beauty AI chat">
          <ChatHeader onClose={() => {
            track("modal_close", { productHandle: detectProductHandleSafe() });
            setOpen(false);
          }} />
          <MessageList
            messages={messages}
            onSuggested={(q) => {
              // Greeting-state chip click: track BEFORE firing so we have
              // the chip text recorded even if the send fails.
              track("chip_click", {
                source: "suggested-chip",
                properties: { chip_text: q },
              });
              setInput(q);
              // Send immediately so the chip behaves like a one-tap action.
              // We rebuild via setInput then call send via microtask so the
              // input state is current.
              queueMicrotask(() => {
                setInput("");
                void sendWithText(q, detectProductHandleSafe());
              });
            }}
          />
          {error && <div class="error-banner">{error}</div>}
          <Composer
            value={input}
            onInput={setInput}
            onSend={send}
            disabled={sending}
          />
          <ChatDisclaimer />
        </div>
      )}
    </>
  );
}

/**
 * AI disclosure shown under the composer.
 *
 * Why this exists:
 *   - Sets shopper expectations that responses are AI-generated, not human
 *     beauty advisors — important for trust + compliance.
 *   - Discloses transcript retention so we can train + run QA against the
 *     agent without surprising the shopper.
 *   - Links to AMP's Privacy Policy and Terms of Service so the legal
 *     pointers are one click away, not buried in the footer.
 *
 * The wording is intentionally close to the AMP Beauty pattern Sephora
 * popularized. We swap "Beauty Insider Account" → "AMP Beauty account"
 * because that's the real loyalty/account surface for this storefront.
 *
 * Styled as small, muted text so it's present without competing with the
 * composer for attention.
 */
function ChatDisclaimer() {
  return (
    <div class="disclaimer" role="note" aria-label="AI chat disclosure">
      Responses are generated using artificial intelligence (AI). By using this chatbot,
      you acknowledge that recommendations are AI-generated and may reflect individual
      preferences or needs, to the extent that you are logged in to your AMP Beauty
      account and have existing beauty preferences saved. We may maintain a transcript
      of chats for quality assurance and to train our AI models to provide better results.
      See our{" "}
      <a href="/policies/privacy-policy" target="_top" rel="noopener">Privacy Policy</a>{" "}
      and{" "}
      <a href="/policies/terms-of-service" target="_top" rel="noopener">Terms of Use</a>{" "}
      for more information.
    </div>
  );
}

function formatToolNote(info: { tool: string; query?: string }): string {
  switch (info.tool) {
    case "search_products":
      return info.query ? `Searching: ${info.query}` : "Searching products…";
    case "get_product_details":
      return "Looking up product details…";
    case "get_faq":
      return "Checking AMP policies…";
    case "get_ingredient_info":
      return "Looking up that ingredient…";
    case "find_products_with_ingredient":
      return "Finding products with that ingredient…";
    case "find_products_without_ingredient":
      return "Filtering out that ingredient…";
    default:
      return `Working: ${info.tool}`;
  }
}

function ChatHeader(props: { onClose: () => void }) {
  return (
    <div class="header">
      <div style={{ flex: 1 }}>
        <div class="header-title">AMP Beauty</div>
        <div class="header-subtitle">AI shopping concierge</div>
      </div>
      <button class="close-btn" aria-label="Close chat" onClick={props.onClose}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

const SUGGESTED_QUESTIONS = [
  "What's a good moisturizer for sensitive skin?",
  "Show me products with niacinamide",
  "Sulfate-free shampoos for curly hair",
];

function MessageList(props: {
  messages: ChatMessage[];
  onSuggested: (q: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Auto-scroll to bottom on new messages.
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [props.messages]);

  if (props.messages.length === 0) {
    return (
      <div class="messages" ref={ref}>
        <div class="greeting">
          <div class="greeting-text">Hi, I'm AMP.</div>
          <div class="greeting-sub">
            Ask me about products, ingredients, or what fits your skin or hair. I know AMP's full catalog.
          </div>
          <div class="suggested">
            <div class="suggested-label">Try asking</div>
            {SUGGESTED_QUESTIONS.map((q) => (
              <button key={q} class="chip" onClick={() => props.onSuggested(q)}>
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="messages" ref={ref}>
      {props.messages.map((m) => (
        <Message key={m.id} message={m} />
      ))}
    </div>
  );
}

function Message(props: { message: ChatMessage }) {
  const m = props.message;
  if (m.sender === "user") {
    return <div class="message user">{m.text}</div>;
  }
  // assistant
  return (
    <div class="message assistant">
      {m.toolNotes && m.toolNotes.length > 0 && (
        <div class="tool-notes">
          {m.toolNotes.map((n, i) => (
            <div class="tool-note" key={i}>{n}</div>
          ))}
        </div>
      )}
      {m.text && <div class="message-text">{renderMarkdown(m.text)}</div>}
      {m.cards && m.cards.length > 0 && (
        <div class="cards">
          {m.cards.map((c, i) =>
            c.kind === "product" ? (
              <ProductCard key={i} card={c.data} />
            ) : (
              <IngredientCard key={i} card={c.data} />
            )
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Product recommendation card with an inline Add-to-Bag button.
 *
 * The card itself is still a link to the PDP (image + title + vendor +
 * price act as the click target). The Add-to-Bag button stops propagation
 * so tapping it doesn't navigate; instead it calls the storefront's AJAX
 * cart endpoints and nudges the theme's cart drawer open.
 *
 * Three button states:
 *   idle    → "Add to bag"               (AMP purple, clickable)
 *   adding  → "Adding…"                  (disabled, subtle pulse)
 *   added   → "✓ Added"                  (success, 2.5s then resets)
 *   error   → "{message} — try again"    (red, clickable)
 */
function ProductCard(props: { card: ProductCardT }) {
  const c = props.card;
  const price =
    c.price_min === c.price_max
      ? `$${c.price_min}`
      : `$${c.price_min}–$${c.price_max}`;

  type AddState =
    | { kind: "idle" }
    | { kind: "adding" }
    | { kind: "added" }
    | { kind: "error"; message: string };
  const [state, setState] = useState<AddState>({ kind: "idle" });

  // Reset to idle a few seconds after success so the button is usable again.
  // (Some shoppers add more than one product from a single chat reply.)
  useEffect(() => {
    if (state.kind !== "added") return;
    const t = window.setTimeout(() => setState({ kind: "idle" }), 2500);
    return () => window.clearTimeout(t);
  }, [state.kind]);

  async function handleAddToBag(e: Event): Promise<void> {
    // Don't let the click navigate to the PDP — this button is its own action.
    e.preventDefault();
    e.stopPropagation();
    if (state.kind === "adding") return;

    // Track the click before the fetch, regardless of outcome. This gives
    // a clean "intent" signal — even if the cart-add ultimately fails,
    // the shopper TRIED, and that's what funnel analysis cares about.
    track("add_to_cart_click", {
      source: "card-add-btn",
      productHandle: c.handle,
    });

    setState({ kind: "adding" });
    try {
      const item = await addToCartByHandle(c.handle);
      setState({ kind: "added" });
      track("add_to_cart_success", {
        source: "card-add-btn",
        productHandle: c.handle,
        properties: { variant_id: item.id, price: item.price },
      });
    } catch (err) {
      const message =
        err instanceof CartAddError
          ? // For stock errors Shopify gives a useful description; surface it.
            err.stage === "add-to-cart" && err.status === 422
            ? "Out of stock"
            : "Couldn't add"
          : "Couldn't add";
      setState({ kind: "error", message });
      track("add_to_cart_error", {
        source: "card-add-btn",
        productHandle: c.handle,
        properties: {
          stage: err instanceof CartAddError ? err.stage : "unknown",
          status: err instanceof CartAddError ? err.status : undefined,
          message,
        },
      });
    }
  }

  // Click handler on the card link (image + title area) — distinct from
  // the Add to Bag button. We track this as the "they want more info,
  // taking them to the PDP" signal.
  function handleCardLinkClick(): void {
    track("product_card_click", {
      source: "card-link",
      productHandle: c.handle,
    });
  }

  const buttonLabel =
    state.kind === "adding"
      ? "Adding…"
      : state.kind === "added"
      ? "✓ Added"
      : state.kind === "error"
      ? `${state.message} — try again`
      : "Add to bag";

  return (
    <a class="card" href={`/products/${c.handle}`} target="_top" onClick={handleCardLinkClick}>
      {c.image_url && (
        <div class="card-image" style={{ backgroundImage: `url(${c.image_url})` }} />
      )}
      <div class="card-body">
        <div class="card-title">{c.title}</div>
        {c.vendor && <div class="card-vendor">{c.vendor}</div>}
        <div class="card-price">{price}</div>
        <button
          type="button"
          class={`card-add-btn card-add-btn--${state.kind}`}
          onClick={handleAddToBag}
          disabled={state.kind === "adding"}
          aria-live="polite"
        >
          {buttonLabel}
        </button>
      </div>
    </a>
  );
}

function IngredientCard(props: { card: IngredientCardT }) {
  const c = props.card;
  return (
    <div class="card" style={{ cursor: "default" }}>
      <div class="card-body">
        <div class="card-title">{c.display_name}</div>
        <div class="card-vendor">
          {c.function_category ?? "ingredient"}
          {c.origin ? ` • ${c.origin}` : ""}
        </div>
        {c.benefits.length > 0 && (
          <div class="card-price" style={{ color: "#666" }}>
            {c.benefits.slice(0, 3).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

function Composer(props: {
  value: string;
  onInput: (v: string) => void;
  onSend: () => void;
  disabled: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the textarea to fit content (cap at CSS max-height).
  useEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = "auto";
    ref.current.style.height = `${ref.current.scrollHeight}px`;
  }, [props.value]);

  return (
    <div class="composer">
      <textarea
        ref={ref}
        rows={1}
        value={props.value}
        placeholder="Ask about products, ingredients…"
        disabled={props.disabled}
        onInput={(e) => props.onInput((e.target as HTMLTextAreaElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            props.onSend();
          }
        }}
      />
      <button
        class="send-btn"
        aria-label="Send"
        disabled={props.disabled || !props.value.trim()}
        onClick={props.onSend}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      </button>
    </div>
  );
}
